import axiosInstance from '../variables/api/axiosInstance';
import { decouperDossiers } from '../pages/conversations/utils';
import {
  LikeOutlined, CheckCircleOutlined, EyeOutlined, QuestionCircleOutlined,
} from '@ant-design/icons';
import { avecLibelleTraduit } from '../i18n/libelles';

/**
 * Discussions internes entre agents, attachées à un dossier.
 *
 * QUATRIÈME CANAL, ET LE SEUL QUI NE SORT PAS DU BACK-OFFICE
 *  - `messagerie`          : client IMAP, part vers des boîtes tierces ;
 *  - `mobileService`       : fil avec la victime, arrive dans son application ;
 *  - échanges bilatéraux   : vers un organisme homologue ;
 *  - ICI                   : entre collègues, et rien n'en sort.
 * Ne jamais rebrancher un composant de ce module sur l'un des trois autres : un
 * doute écrit sur un procès-verbal partirait chez la victime ou chez l'assureur.
 *
 * GÉNÉRIQUE PAR CONSTRUCTION. Toutes les routes portent (entiteType, entiteId) :
 * le fil se rebranche sur un dossier bilatéral ou une expertise sans une ligne de
 * plus ici - seul le résolveur backend décide des types réellement ouverts.
 */

/** Agrégats pouvant porter un fil. Seul SINISTRE est ouvert côté serveur à ce jour. */
export const ENTITE = {
  SINISTRE: 'SINISTRE',
  DOSSIER_BILATERAL: 'DOSSIER_BILATERAL',
  EXPERTISE: 'EXPERTISE',
  RECLAMATION: 'RECLAMATION',
};

/** Élément précis d'un dossier auquel un message peut être accroché. */
export const ANCRE = {
  VICTIME: 'VICTIME',
  OFFRE: 'OFFRE',
  DOCUMENT: 'DOCUMENT',
  DELAI: 'DELAI',
  PV: 'PV',
  REGLEMENT: 'REGLEMENT',
  EXPERTISE: 'EXPERTISE',
};

/**
 * Les quatre emblèmes, et leur icône d'affichage.
 *
 * La correspondance vit ici et non en base : le serveur stocke un code, le client
 * choisit l'icône. Changer d'icône ne demande alors aucune migration, et deux
 * clients (web, mobile un jour) peuvent présenter la même donnée à leur façon.
 *
 * DES ICONES, ET NON DES EMOJIS : le dessin d'un emoji est celui de la police du
 * système, il change d'un poste à l'autre et Windows ne possède pas tous les
 * glyphes. Une icône AntD rend la même chose partout.
 */
export const EMBLEMES = avecLibelleTraduit([
  { code: 'POUCE', glyphe: <LikeOutlined /> },
  { code: 'OK', glyphe: <CheckCircleOutlined /> },
  { code: 'VU', glyphe: <EyeOutlined /> },
  { code: 'QUESTION', glyphe: <QuestionCircleOutlined /> },
], 'referentiels:emblemesDiscussion', { cle: 'code', champ: 'titre' });

const base = (entiteType, entiteId) => `/v1/discussions/${entiteType}/${entiteId}`;

const discussionService = {

  /**
   * Une page du fil, la plus récente d'abord.
   *
   * Lire ne marque RIEN côté serveur - c'est l'inverse du fil usager, dont la
   * lecture marque et qui, pour cette raison, ne peut être ni rafraîchi ni monté
   * par anticipation. Ici le panneau se sonde librement ; l'avance du repère
   * passe par `marquerLu`.
   */
  messages: async (entiteType, entiteId, { page = 0, size = 25, ancreType, ancreTrackingId } = {}) => {
    const { data } = await axiosInstance.get(`${base(entiteType, entiteId)}/messages`, {
      params: { page, size, ancreType, ancreTrackingId },
    });
    return { items: data?.data ?? [], total: data?.totalElements ?? 0, page: data?.currentPage ?? 0 };
  },

  ecrire: async (entiteType, entiteId, payload) => {
    const { data } = await axiosInstance.post(`${base(entiteType, entiteId)}/messages`, payload);
    return data?.data;
  },

  /**
   * Ce que le rail replié affiche. Sondé sur chaque dossier ouvert : `skipErrorToast`
   * parce qu'un échec de sondage ne doit pas interrompre l'agent avec une alerte.
   */
  resume: async (entiteType, entiteId) => {
    const { data } = await axiosInstance.get(`${base(entiteType, entiteId)}/resume`, { skipErrorToast: true });
    return data?.data;
  },

  marquerLu: (entiteType, entiteId, dernierMessageLu) =>
    axiosInstance.post(`${base(entiteType, entiteId)}/lecture`, { dernierMessageLu }, { skipErrorToast: true }),

  mentionnables: async (entiteType, entiteId, q) => {
    const { data } = await axiosInstance.get(`${base(entiteType, entiteId)}/mentionnables`, {
      params: { q }, skipErrorToast: true,
    });
    return data?.data ?? [];
  },

  modifier: async (messageTrackingId, corps) => {
    const { data } = await axiosInstance.patch(`/v1/discussions/messages/${messageTrackingId}`, { corps });
    return data?.data;
  },

  supprimer: async (messageTrackingId) => {
    const { data } = await axiosInstance.delete(`/v1/discussions/messages/${messageTrackingId}`);
    return data?.data;
  },

  /** Bascule : poser l'emblème s'il est absent, le retirer sinon. */
  basculerReaction: async (messageTrackingId, embleme) => {
    const { data } = await axiosInstance.put(
      `/v1/discussions/messages/${messageTrackingId}/reactions/${embleme}`);
    return data?.data;
  },

  epingler: async (messageTrackingId, valeur) => {
    const { data } = await axiosInstance.patch(
      `/v1/discussions/messages/${messageTrackingId}/epingle`, null, { params: { valeur } });
    return data?.data;
  },

  mesFils: async ({ page = 0, size = 20 } = {}) => {
    const { data } = await axiosInstance.get('/v1/discussions/mes-fils', { params: { page, size } });
    return { items: data?.data ?? [], total: data?.totalElements ?? 0 };
  },

  mesMentions: async ({ page = 0, size = 10 } = {}) => {
    const { data } = await axiosInstance.get('/v1/discussions/mes-mentions', {
      params: { page, size }, skipErrorToast: true,
    });
    return { items: data?.data ?? [], total: data?.totalElements ?? 0 };
  },

  compterMentionsNonLues: async () => {
    const { data } = await axiosInstance.get('/v1/discussions/mes-mentions/non-lues', { skipErrorToast: true });
    return data?.data ?? 0;
  },
};

/**
 * Découpe un corps en fragments texte / mention / dossier.
 *
 * Le serveur renvoie le texte tel qu'il a été écrit, marqueurs compris. Le rendu
 * doit rester du TEXTE : pas de `dangerouslySetInnerHTML`, pas de Markdown. Ce qui
 * est saisi dans un dossier d'indemnisation s'affiche tel quel - et un corps de
 * message est, par nature, une chaîne qu'un utilisateur contrôle entièrement.
 *
 * Un dossier cité par `#` (`#[2026/0363/CI](sinistre:uuid)`) suit le format des
 * conversations : même marqueur, même puce, même ouverture directe du dossier.
 *
 * @returns {{type: 'texte'|'mention'|'dossier', valeur: string, cible?: string, sinistreTrackingId?: string}[]}
 */
export const decouperCorps = (corps) => {
  if (!corps) return [];
  const motif = /@\[([^\]\r\n]{1,120})\]\((utilisateur|role):([A-Za-z0-9_-]{1,60})\)/g;
  const fragments = [];
  const pousserTexte = (texte) => decouperDossiers(texte).forEach((f) => fragments.push(f));
  let curseur = 0;
  let m = motif.exec(corps);
  while (m !== null) {
    if (m.index > curseur) pousserTexte(corps.slice(curseur, m.index));
    fragments.push({ type: 'mention', valeur: m[1], cible: `${m[2]}:${m[3]}` });
    curseur = m.index + m[0].length;
    m = motif.exec(corps);
  }
  if (curseur < corps.length) pousserTexte(corps.slice(curseur));
  return fragments;
};

/**
 * Marqueur à insérer dans la zone de saisie pour une entrée du menu `@`.
 *
 * Le libellé écrit ici n'est qu'un affichage : le serveur le réécrit à partir de
 * l'UUID avant d'enregistrer. C'est ce qui interdit d'afficher « Directeur
 * général » sur une puce qui pointe vers quelqu'un d'autre.
 */
export const marqueurPour = (mentionnable) =>
  mentionnable.cible === 'UTILISATEUR'
    ? `@[${mentionnable.libelle}](utilisateur:${mentionnable.utilisateurTrackingId})`
    : `@[${mentionnable.libelle}](role:${mentionnable.cible})`;

export default discussionService;
